import React from "react";
import { Trophy, Award } from "lucide-react";
import { PageHero, Reveal, Stagger, StaggerItem, Overline, SectionHeading, CTAButton } from "@/components/primitives";
import LeadDialog from "@/components/forms/LeadDialog";
import { AWARD_CATEGORIES } from "@/data/site";

export default function Awards() {
  return (
    <>
      <PageHero
        overline="Recognition"
        title="Bharat Ethanol Leadership Awards."
        subtitle="The night the industry honours its own — the producers, innovators and pioneers building India's biofuel economy."
        image="/assets/site/awards-gala-celebration.jpg"
        imageFallback="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
      />

      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Ten categories, one stage" title="Excellence across the ethanol ecosystem." />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {AWARD_CATEGORIES.map((c, i) => (
              <StaggerItem key={c}>
                <div className="group flex items-center gap-4 rounded-2xl border border-border bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-ethanolOrange/40 hover:shadow-xl">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald2/10 font-display text-sm font-bold text-emeraldDeep">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-semibold text-navy">{c}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
          <Reveal>
            <Overline className="mb-4 justify-center">Entries</Overline>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">Put a name forward.</h2>
            <p className="mt-5 text-lg text-navy/70">Nominations are open across all ten categories. Nominate your organisation, a partner, or a leader who's moved this industry.</p>
            <div className="mt-8 flex justify-center">
              <LeadDialog
                formType="nomination"
                title="Submit a Nomination"
                description="Tell us who you're nominating and why."
                ctaLabel="Submit nomination"
                successMsg="Nomination received. Thank you."
                extraFields={[{ key: "nominee", placeholder: "Nominee / organisation" }, { key: "category", placeholder: "Award category" }]}
                trigger={<CTAButton data-testid="submit-nomination"><Award className="h-4 w-4" /> Submit a Nomination</CTAButton>}
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy grain py-20 md:py-28">
        <div className="pointer-events-none absolute right-1/4 top-0 h-72 w-72 rounded-full bg-ethanolOrange/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 md:px-12">
          <Reveal>
            <Overline light className="mb-4">The gala</Overline>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">The deals continue after dark.</h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              Awards, curated entertainment and a premium dinner — the signature Tefla's evening, built so CXOs can talk freely and partnerships start across the table.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
